'use strict';

import { DOM } from '../helpers/DOM.js';
import { Helper } from '../helpers/Helper.js';

export class DonationTribute {
	#donationTributeCheckbox;
	#donationTributeWrapper;
	#donationMultipleRecipients;
	#donationTributeCheckboxValue;
	#donationTributeTypeContainer;
	#typeSwitcher;
	#switcherButtons = [];
	#donationTributeItems;
	#donationRecipientItems;

	constructor() {
		this.#cacheElements();
		this.#initTributeToggle();
		this.#initTypeSwitcher();
		this.#initLocaleLabels();
		this.#initAddressLabels();
		this.#initMessageCharCounters();
		this.#initHonoreeEmailRequirement();
	}

	#cacheElements() {
		this.#donationTributeCheckbox = document.getElementById(
			'donation_tribute'
		);
		this.#donationTributeWrapper = document.querySelector(
			'div.levcharity_form__field.donation_tribute_wrapper'
		);
		this.#donationMultipleRecipients = document.querySelector(
			'div.levcharity_form__field.allow_multiple_recipients'
		);
		this.#donationTributeCheckboxValue = document.querySelector(
			'input[name="donation_tribute_checkbox_value"]'
		);
		this.#donationTributeTypeContainer = DOM.el(
			'.levcharity-donation-tribute-type-container'
		);
		this.#typeSwitcher = DOM.el('.donation-tribute-switcher');
		if ( this.#typeSwitcher ) {
			this.#switcherButtons = Array.from(
				DOM.els('.donation-tribute-type-button', this.#typeSwitcher)
			);
		}
		this.#donationTributeItems = DOM.el('.donation_tribute_items_wrapper');
		this.#donationRecipientItems = DOM.el(
			'.donation_recipent_items_wrapper'
		);
	}

	#initTributeToggle() {
		if ( ! this.#donationTributeCheckbox || ! this.#donationTributeWrapper ) {
			return;
		}

		const checkboxContainer = this.#donationTributeCheckbox.closest(
			'.donation_tribute_checkbox'
		);
		const isCheckboxHidden =
			checkboxContainer &&
			window.getComputedStyle( checkboxContainer ).display === 'none';
		const isWrapperVisible =
			window.getComputedStyle( this.#donationTributeWrapper ).display !==
			'none';

		if ( isCheckboxHidden && isWrapperVisible ) {
			this.#donationTributeCheckbox.checked = true;
			this.#donationTributeWrapper.style.display = '';

			if ( this.#donationTributeCheckboxValue ) {
				this.#donationTributeCheckboxValue.value = 'on';
			}
		} else {
			this.#donationTributeCheckbox.checked =
				this.#donationTributeCheckbox.defaultChecked;
		}

		if ( this.#donationTributeCheckbox.checked ) {
			this.#donationTributeWrapper.style.display = '';

			if ( this.#donationMultipleRecipients ) {
				this.#donationMultipleRecipients.style.display = '';
			}

			if ( this.#donationTributeCheckboxValue ) {
				if ( ! this.#donationTributeCheckboxValue.hasAttribute('value') ) {
					this.#donationTributeCheckboxValue.setAttribute('value', 'on');
				} else {
					this.#donationTributeCheckboxValue.value = 'on';
				}
			}
		}


		this.#donationTributeCheckbox.addEventListener( 'change', ( event ) => {
			const isChecked = event.target.checked;
			this.#donationTributeWrapper.style.display = isChecked ? '' : 'none';
			if ( this.#donationMultipleRecipients ) {
				this.#donationMultipleRecipients.style.display = isChecked
					? ''
					: 'none';
			}
			if ( this.#donationTributeCheckboxValue ) {
				this.#donationTributeCheckboxValue.value = isChecked ? 'on' : '';
			}
		} );
	}

	#initTypeSwitcher() {
		if (
			! this.#donationTributeTypeContainer ||
			! this.#typeSwitcher ||
			this.#switcherButtons.length === 0
		) {
			return;
		}

		if ( this.#donationTributeItems ) {
			this.#donationTributeItems.style.display = 'none';
		}

		// Activating a type clears the other one, so start on the type the donor already chose.
		const selectedType = this.#typeSwitcher.dataset.selectedType;
		const selectedClass = selectedType === 'electronically_type'
			? 'tribute-type-electronically'
			: 'tribute-type-physically';

		this.#switcherButtons.forEach( ( button ) => {
			if ( button.classList.contains( selectedClass ) ) {
				this.#activateButton( button );
			}

			button.addEventListener( 'click', () => {
				this.#activateButton( button );
			} );
		} );
	}

	#activateButton( button ) {
		const radioInput = button.querySelector( 'input[type="radio"]' );
		if ( ! radioInput ) {
			return;
		}

		radioInput.checked = true;
		const radioInputValue = radioInput.value;

		if ( radioInputValue === 'physically_type' ) {
			if ( ! this.#typeSwitcher.classList.contains( 'physically_type' ) ) {
				this.#typeSwitcher.classList.add( 'physically_type' );
			}
			this.#typeSwitcher.classList.remove( 'electronically_type' );

			this.#toggleInputs( this.#donationRecipientItems, true );
			this.#toggleInputs( this.#donationTributeItems, false, true );

			this.#resetValidationState( this.#donationTributeItems );
		} else {
			this.#typeSwitcher.classList.remove( 'physically_type' );
			this.#typeSwitcher.classList.add( 'electronically_type' );

			this.#toggleInputs( this.#donationRecipientItems, false, true );
			this.#toggleInputs( this.#donationTributeItems, true );

			this.#resetValidationState( this.#donationRecipientItems );
		}
	}

	#toggleInputs( container, enable, clear = false ) {
		if ( ! container ) {
			return;
		}

		const inputs = container.querySelectorAll( 'input, select, textarea' );
		inputs.forEach( ( input ) => {
			input.disabled = ! enable;

			if ( clear ) {
				if ( input.name !== 'donation_tribute_multiple_recipients' ) {
					input.value = '';
				}

				if ( input.type === 'checkbox' || input.type === 'radio' ) {
					input.checked = false;
				}

				if (
					input.tagName.toLowerCase() === 'select' &&
					input.name !== 'donation_tribute_multiple_recipients'
				) {
					input.selectedIndex = 0;
				}
			}
		} );

		container.style.display = enable ? '' : 'none';
	}

	#initAddressLabels() {
		const container = document.querySelector( '.donation-tribute-container' );
		if ( ! container ) {
			return;
		}

		container.addEventListener( 'change', ( event ) => {
			if ( event.target.matches( 'select.donation_tribute_recipient_country' ) ) {
				this.#applyAddressLabels( event.target );
				return;
			}

			// Recipient blocks are cloned after this event, so wait for them to exist.
			if ( event.target.id === 'donation_tribute_multiple_recipients_select' ) {
				setTimeout( () => this.#applyAllAddressLabels( container ), 0 );
			}
		} );

		this.#applyAllAddressLabels( container );
	}

	#applyAllAddressLabels( container ) {
		container
			.querySelectorAll( 'select.donation_tribute_recipient_country' )
			.forEach( ( select ) => this.#applyAddressLabels( select ) );
	}

	#applyAddressLabels( countrySelect ) {
		const scope =
			countrySelect.closest( '.additional-donation-fields' ) ||
			countrySelect.closest( '.donation_recipent_items_wrapper' );

		if ( ! scope ) {
			return;
		}

		let addressLabels = {};
		try {
			addressLabels = JSON.parse( countrySelect.dataset.addressLabels || '{}' );
		} catch ( error ) {
			return;
		}

		const countryLabels = addressLabels[ countrySelect.value ] || {};
		const state = scope.querySelector( 'input.donation_tribute_recipient_state' );
		const zip = scope.querySelector( 'input.donation_tribute_recipient_zip' );

		if ( state ) {
			state.placeholder = countryLabels.state || state.dataset.placeholderDefault || state.placeholder;
		}

		if ( zip ) {
			zip.placeholder = countryLabels.postcode || zip.dataset.placeholderDefault || zip.placeholder;
		}
	}

	#initMessageCharCounters() {
		const container = document.querySelector( '.donation-tribute-container' );
		if ( ! container ) {
			return;
		}

		const maxLength = parseInt(
			container.dataset.tributeMessageMaxLength || '250',
			10
		);

		container
			.querySelectorAll(
				'.donation_tribute_certificate_message, .donation_tribute_card_message'
			)
			.forEach( ( textarea ) => {
				const counter = textarea
					.closest( '.levcharity-tribute-message-field__input' )
					?.querySelector( '.levcharity-tribute-message-char-count' );

				if ( ! counter ) {
					return;
				}

				const updateCounter = () => {
					counter.textContent = `${ textarea.value.length }/${ maxLength }`;
				};

				updateCounter();
				textarea.addEventListener( 'input', updateCounter );
			} );
	}

	#initLocaleLabels() {
		const localeLabels = document.querySelectorAll( '.levcharity-locale-label' );
		if ( ! localeLabels.length ) {
			return;
		}

		const currencySelect = document.querySelector( '[name="user_paying_currency"]' );
		if ( ! currencySelect ) {
			return;
		}

		const updateLabels = ( currency ) => {
			const isAlt = [ 'GBP', 'CAD' ].includes( currency?.toUpperCase() );
			localeLabels.forEach( ( label ) => {
				const defaultText = label.dataset.labelDefault;
				const altText = label.dataset.labelAlt;
				if ( ! defaultText || ! altText ) {
					return;
				}
				const asterisk = label.querySelector( 'abbr.required' );
				label.textContent = isAlt ? altText : defaultText;
				label.append( ' ' );
				if ( asterisk ) {
					label.appendChild( asterisk );
				}
			} );
		};

		currencySelect.addEventListener( 'change', ( event ) => {
			updateLabels( event.target.value );
		} );

		updateLabels( currencySelect.value );
	}

	#initHonoreeEmailRequirement() {
		const container = document.querySelector( '.donation-tribute-container' );
		if ( ! container ) {
			return;
		}

		const update = () => {
			container.querySelectorAll( '.donation_tribute_items_wrapper' ).forEach( ( row ) => {
				const typeSelect = row.querySelector( 'select[name*="[type]"]' );
				const email = row.querySelector( 'input[name*="[certificate_email]"]' );
				const asterisk = row.querySelector(
					'.donation_tribute_certificate_email_wrapper abbr.required'
				);
				const optional = Helper.isInMemoryTributeType( typeSelect?.value || '' );

				if ( asterisk ) {
					asterisk.hidden = optional;
				}
				if ( email ) {
					email.setAttribute( 'aria-required', optional ? 'false' : 'true' );
					if ( optional ) {
						email.classList.remove( 'no-valid' );
					}
				}
			} );
		};

		container.addEventListener( 'change', ( event ) => {
			const target = event.target;
			if ( ! ( target instanceof Element ) ) {
				return;
			}
			if (
				target.matches( 'select[name*="[type]"]' ) ||
				target.id === 'donation_tribute_multiple_recipients_select'
			) {
				update();
			}
		} );

		update();
	}

	#resetValidationState( container ) {
		if ( ! container ) {
			return;
		}

		container
			.querySelectorAll( 'div input, div select, div textarea' )
			.forEach( ( field ) => {
				field.style.border =
					'1px solid var(--lc-color-border) !important';
				field.classList.remove( 'no-valid' );
			} );
	}
}
